import React, { useState, useEffect, useRef } from 'react';
import formularioService from '../../services/formularioService';

import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  ViewModule as ViewModuleIcon,
  List as ListIcon
} from '@mui/icons-material';
// Importar los componentes necesarios
import FormularioSelector from './FormularioSelector';
import FormularioEditor from '../formularios/FormularioEditor';
import DocumentosEspecialidadList from './DocumentosEspecialidadList';
import DocumentosEtapaList from './DocumentosEtapaList';
import { useParams } from 'react-router-dom';

const DocumentosHistoriaView = ({
  historiaClinicaId,
  pacienteId,
  programaId,
  especialidadId,
  etapaActualId,
  onDocumentoCreado,
  onDocumentoEliminado,
  onDocumentoActualizado
}) => {
const { historiaId } = useParams();

  // 2. Agrega el estado faltante para plantillas
  const [vistaActiva, setVistaActiva] = useState('especialidad');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  
  // Otros estados que ya tengas...
  const [mostrarFormularioSelector, setMostrarFormularioSelector] = useState(false);
  const [mostrarEditor, setMostrarEditor] = useState(false);
  const [formularioActualId, setFormularioActualId] = useState(null);
  
  // Referencias existentes
  const isInitialMount = useRef(true);
  const callbackTriggered = useRef(false);
  
  // Marcar que ya no es el montaje inicial después del primer render
 useEffect(() => {
    const cargarPlantillasFormularios = async () => {
      if (!especialidadId) return;
      
      try {
        setLoading(true);
        
        // Usar el servicio de formularios (que sí existe) para cargar por especialidad
        
        const formulariosData = await formularioService.getFormulariosPorEspecialidad(especialidadId);
   
        // Guardar los resultados en el estado de plantillas
        setPlantillas(formulariosData);
      } catch (err) {
        console.error('Error al cargar formularios por especialidad:', err);
        setError('Error al cargar los formularios disponibles');
      } finally {
        setLoading(false);
      }
    };
    
    cargarPlantillasFormularios();
  }, [especialidadId]);
  
    useEffect(() => {
    isInitialMount.current = false;
    return () => {
      isInitialMount.current = true;
    };
  }, []);

  // Manejar cambio de vista
  const handleVistaChange = (vista) => {
    setVistaActiva(vista);
    setMostrarFormularioSelector(false);
    setMostrarEditor(false);
  };
  
  // Manejar evento cuando se crea un documento
  const handleDocumentoCreado = (documento) => {
    if (callbackTriggered.current) return;
    callbackTriggered.current = true;
    
    if (typeof onDocumentoCreado === 'function' && !isInitialMount.current) {
      onDocumentoCreado(documento);
    }
    
    setTimeout(() => {
      callbackTriggered.current = false;
    }, 100);
  };
  
  // Manejar evento cuando se elimina un documento
  const handleDocumentoEliminado = (documentoId) => {
    if (callbackTriggered.current) return;
    callbackTriggered.current = true;
    
    if (typeof onDocumentoEliminado === 'function' && !isInitialMount.current) {
      onDocumentoEliminado(documentoId);
    }
    
    setTimeout(() => {
      callbackTriggered.current = false;
    }, 100);
  };
  
  // Manejar evento cuando se actualiza un documento
  const handleDocumentoActualizado = (documento) => {
    if (callbackTriggered.current) return;
    callbackTriggered.current = true;
    
    if (typeof onDocumentoActualizado === 'function' && !isInitialMount.current) {
      onDocumentoActualizado(documento);
    }
    
    setTimeout(() => {
      callbackTriggered.current = false;
    }, 100);
  };
  
  // Función para seleccionar un formulario
   const handleFormularioSelect = (formularioId) => {
    
    setFormularioActualId(formularioId);
    setMostrarFormularioSelector(false);
    setMostrarEditor(true);
  };
  
  // Función para manejar el guardado de un formulario
  const handleFormularioSave = (resultado) => {
    
    setMostrarEditor(false);
    // Llamar al callback si existe y si no estamos en el montaje inicial
    if (typeof onDocumentoCreado === 'function' && !isInitialMount.current) {
      onDocumentoCreado(resultado);
    }
  };

  
  return (
    <Box>
      {/* Barra de acciones y selector de vista */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AssignmentIcon />}
          onClick={() => setMostrarFormularioSelector(!mostrarFormularioSelector)}
        >
          Completar Formulario
        </Button>
        
        <Box>
          <Button
            variant={vistaActiva === 'especialidad' ? 'contained' : 'outlined'}
            startIcon={<ViewModuleIcon />}
            onClick={() => handleVistaChange('especialidad')}
            sx={{ mr: 1 }}
          >
            Vista por Especialidad
          </Button>
          <Button
            variant={vistaActiva === 'etapas' ? 'contained' : 'outlined'}
            startIcon={<ListIcon />}
            onClick={() => handleVistaChange('etapas')}
          >
            Vista por Etapas
          </Button>
        </Box>
      </Box>
      
      {/* Mostrar error si existe */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Selector de formularios */}
      {mostrarFormularioSelector && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Seleccione un formulario para completar
          </Typography>
          <FormularioSelector 
            especialidadId={especialidadId} 
            historiaId={historiaClinicaId}
            onFormularioSelect={handleFormularioSelect}
          />
        </Paper>
      )}
      
      {/* Editor de formularios */}
      {mostrarEditor && formularioActualId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <FormularioEditor
            formularioId={formularioActualId}
            historiaId={historiaId}
            pacienteId={pacienteId}
            onSave={handleFormularioSave}
            onCancel={() => setMostrarEditor(false)}
          />
        </Paper>
      )}
      
      {/* Vista principal */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {vistaActiva === 'especialidad' ? (
            <DocumentosEspecialidadList
              historiaId={historiaClinicaId}
              especialidadId={especialidadId}
              etapaId={etapaActualId}
              onDocumentoCreado={handleDocumentoCreado}
              onDocumentoEliminado={handleDocumentoEliminado}
              onDocumentoActualizado={handleDocumentoActualizado}
            />
          ) : (
            <DocumentosEtapaList
              historiaId={historiaClinicaId}
              etapaId={etapaActualId}
              onDocumentoCreado={handleDocumentoCreado}
              onDocumentoEliminado={handleDocumentoEliminado}
              onDocumentoActualizado={handleDocumentoActualizado}
            />
          )}
        </>
      )}
    </Box>
  );

};

export default DocumentosHistoriaView;